import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

export interface CompareProduct {
  activities: string[];
  aggregateRating: number | null;
  availableWeights: string[];
  brand: string | null;
  category: string | null;
  dosage: string | null;
  feelings: string[];
  flavors: string[];
  id: number;
  imageUrl: string;
  kind: string;
  name: string;
  originalPrice: string | null;
  percentCbd: number | null;
  percentThc: number | null;
  price: string | null;
  reviewCount: number | null;
  /** PDP url — when present, modal product links render as real anchors */
  url?: string | null;
}

export interface CompareHandlers {
  addToCart?: () => void;
  view?: () => void;
}

export interface CompareContextValue {
  clearAll: () => void;
  closeModal: () => void;
  /** Close the modal in response to a user dismissal (fires onCloseModal) */
  dismissModal: () => void;
  enabled: boolean;
  /** Stable getter for a product's selection — safe to call from event handlers (do not read during render; use `isSelected`). */
  getIsSelected: (id: number) => boolean;
  /** Stable getter for the current selection count — safe to call from event handlers. */
  getProductCount: () => number;
  isActive: boolean;
  isAtMax: boolean;
  isModalOpen: boolean;
  isSelected: (id: number) => boolean;
  openModal: () => void;
  productCount: number;
  products: CompareProduct[];
  removeProduct: (id: number) => void;
  setActive: (active: boolean) => void;
  toggleActive: () => void;
  toggleProduct: (
    product: CompareProduct,
    handlers?: CompareHandlers
  ) => boolean;
  triggerAddToCart: (id: number) => void;
  triggerView: (id: number) => void;
}

export const MAX_COMPARE = 5;

/**
 * Volatile state that changes as products are (de)selected — subscribing to it
 * re-renders the consumer. Use this in leaves (the toggle, the bar).
 */
type CompareStateValue = Pick<
  CompareContextValue,
  | 'products'
  | 'productCount'
  | 'isActive'
  | 'isAtMax'
  | 'isModalOpen'
  | 'isSelected'
>;

/**
 * Stable actions + getters. This object keeps a constant identity
 * across state changes, so components that only need to *act* (e.g. a product
 * card building its toggle handler) never re-render when the selection changes.
 */
type CompareActionsValue = Omit<CompareContextValue, keyof CompareStateValue>;

const defaultActions: CompareActionsValue = {
  enabled: false,
  getIsSelected: () => false,
  getProductCount: () => 0,
  toggleProduct: () => false,
  removeProduct: () => {},
  clearAll: () => {},
  openModal: () => {},
  closeModal: () => {},
  dismissModal: () => {},
  setActive: () => {},
  toggleActive: () => {},
  triggerAddToCart: () => {},
  triggerView: () => {},
};

const defaultState: CompareStateValue = {
  products: [],
  productCount: 0,
  isActive: false,
  isAtMax: false,
  isModalOpen: false,
  isSelected: () => false,
};

const defaultCompareContext: CompareContextValue = {
  ...defaultActions,
  ...defaultState,
};

const CompareActionsContext = createContext<CompareActionsValue | undefined>(
  undefined
);
const CompareStateContext = createContext<CompareStateValue | undefined>(
  undefined
);

type CompareAction =
  | { product: CompareProduct; type: 'ADD_PRODUCT' }
  | { id: number; type: 'REMOVE_PRODUCT' }
  | { type: 'CLEAR_ALL' };

function compareReducer(
  state: CompareProduct[],
  action: CompareAction
): CompareProduct[] {
  switch (action.type) {
    case 'ADD_PRODUCT': {
      if (state.length >= MAX_COMPARE) return state;
      if (state.some((p) => p.id === action.product.id)) return state;
      return [...state, action.product];
    }
    case 'REMOVE_PRODUCT':
      return state.filter((p) => p.id !== action.id);
    case 'CLEAR_ALL':
      return [];
    default:
      return state;
  }
}

interface CompareProviderProps {
  children: ReactNode;
  /** Exit compare mode automatically once the last product is deselected */
  deactivateWhenEmpty?: boolean;
  enabled?: boolean;
  onActiveChange?: (isActive: boolean) => void;
  /** Called when the user cancels the comparison via "Clear all" */
  onClearAll?: (productCount: number) => void;
  /** Called when the user dismisses the compare modal (close button, overlay, esc) */
  onCloseModal?: (productCount: number) => void;
  /** Called when the user opens the compare modal */
  onOpenModal?: (productCount: number, products: CompareProduct[]) => void;
}

export const CompareProvider = ({
  children,
  deactivateWhenEmpty = false,
  enabled = true,
  onActiveChange,
  onClearAll,
  onCloseModal,
  onOpenModal,
}: CompareProviderProps) => {
  const [products, dispatch] = useReducer(compareReducer, []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const onActiveChangeRef = useRef(onActiveChange);
  const onClearAllRef = useRef(onClearAll);
  const onCloseModalRef = useRef(onCloseModal);
  const onOpenModalRef = useRef(onOpenModal);

  const handlersRef = useRef<Map<number, CompareHandlers>>(new Map());

  // Live values read by the stable callbacks below, so those callbacks keep a
  // constant identity across renders.
  const productsRef = useRef(products);
  const deactivateWhenEmptyRef = useRef(deactivateWhenEmpty);
  useEffect(() => {
    productsRef.current = products;
    deactivateWhenEmptyRef.current = deactivateWhenEmpty;
  }, [products, deactivateWhenEmpty]);

  const setActive = useCallback((active: boolean) => {
    setIsActive((prev) => {
      if (prev !== active) onActiveChangeRef.current?.(active);
      return active;
    });
  }, []);

  const toggleActive = useCallback(() => {
    setIsActive((prev) => {
      const next = !prev;
      onActiveChangeRef.current?.(next);
      return next;
    });
  }, []);

  // Reactive: read during render to drive the toggle's displayed state.
  const isSelected = useCallback(
    (id: number) => products.some((p) => p.id === id),
    [products]
  );

  // Stable getters — read the live ref, never call during render.
  const getIsSelected = useCallback(
    (id: number) => productsRef.current.some((p) => p.id === id),
    []
  );
  const getProductCount = useCallback(() => productsRef.current.length, []);

  const toggleProduct = useCallback(
    (product: CompareProduct, handlers?: CompareHandlers): boolean => {
      const current = productsRef.current;
      if (current.some((p) => p.id === product.id)) {
        handlersRef.current.delete(product.id);
        dispatch({ type: 'REMOVE_PRODUCT', id: product.id });
        if (deactivateWhenEmptyRef.current && current.length === 1) {
          setActive(false);
        }
        return true;
      }
      if (current.length >= MAX_COMPARE) {
        return false;
      }
      if (handlers) handlersRef.current.set(product.id, handlers);
      dispatch({ type: 'ADD_PRODUCT', product });
      return true;
    },
    [setActive]
  );

  const removeProduct = useCallback(
    (id: number) => {
      handlersRef.current.delete(id);
      dispatch({ type: 'REMOVE_PRODUCT', id });
      if (deactivateWhenEmptyRef.current && productsRef.current.length === 1) {
        setActive(false);
      }
    },
    [setActive]
  );

  const clearAll = useCallback(() => {
    onClearAllRef.current?.(productsRef.current.length);
    handlersRef.current.clear();
    dispatch({ type: 'CLEAR_ALL' });
    setIsModalOpen(false);
    if (deactivateWhenEmptyRef.current) setActive(false);
  }, [setActive]);

  const triggerAddToCart = useCallback((id: number) => {
    handlersRef.current.get(id)?.addToCart?.();
  }, []);

  const triggerView = useCallback((id: number) => {
    handlersRef.current.get(id)?.view?.();
  }, []);

  const openModal = useCallback(() => {
    onOpenModalRef.current?.(productsRef.current.length, productsRef.current);
    setIsModalOpen(true);
  }, []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const dismissModal = useCallback(() => {
    onCloseModalRef.current?.(productsRef.current.length);
    setIsModalOpen(false);
  }, []);

  // Stable across state changes (all members have constant identity), so
  // `useCompareActions()` consumers don't re-render when the selection changes.
  const actions = useMemo<CompareActionsValue>(
    () =>
      enabled
        ? {
            enabled: true,
            getIsSelected,
            getProductCount,
            toggleProduct,
            removeProduct,
            clearAll,
            openModal,
            closeModal,
            dismissModal,
            setActive,
            toggleActive,
            triggerAddToCart,
            triggerView,
          }
        : defaultActions,
    [
      enabled,
      getIsSelected,
      getProductCount,
      toggleProduct,
      removeProduct,
      clearAll,
      openModal,
      closeModal,
      dismissModal,
      setActive,
      toggleActive,
      triggerAddToCart,
      triggerView,
    ]
  );

  const state = useMemo<CompareStateValue>(
    () =>
      enabled
        ? {
            products,
            productCount: products.length,
            isActive,
            isAtMax: products.length >= MAX_COMPARE,
            isModalOpen,
            isSelected,
          }
        : defaultState,
    [enabled, products, isActive, isModalOpen, isSelected]
  );

  return (
    <CompareActionsContext.Provider value={actions}>
      <CompareStateContext.Provider value={state}>
        {children}
      </CompareStateContext.Provider>
    </CompareActionsContext.Provider>
  );
};

function warnIfOutsideProvider(isOutside: boolean) {
  if (isOutside && process.env['NODE_ENV'] === 'development') {
    // eslint-disable-next-line no-console
    console.warn(
      'useCompare was called outside of <CompareProducts> (CompareProvider). Compare features will be inert.'
    );
  }
}

/**
 * Stable compare actions + event-time getters. Prefer this in components that
 * only need to *act* (e.g. building a toggle handler) — it does not re-render
 * when the selection changes. Read displayed selection via `useCompare`.
 */
export const useCompareActions = (): CompareActionsValue => {
  const actions = useContext(CompareActionsContext);
  warnIfOutsideProvider(actions === undefined);
  return actions ?? defaultActions;
};

export const useCompare = (): CompareContextValue => {
  const actions = useContext(CompareActionsContext);
  const state = useContext(CompareStateContext);
  warnIfOutsideProvider(actions === undefined || state === undefined);
  if (actions === undefined || state === undefined) {
    return defaultCompareContext;
  }
  return { ...actions, ...state };
};
